import { Select } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import React, { memo } from "react";
import { WhiteDoubleArrow } from "../views/comparison_views/molecules/WhiteDoubleArrow";
import { FormHelperStyledError } from "../views/comparison_views/molecules/ui-utils";

const StandardSelectComponent = ({
  options,
  value,
  onChange,
  id,
  name,
  width = 200,
}) => {
  return (
    <FormControl sx={{ m: 1, minWidth: width, marginRight: 0 }} size="small">
      <Select
        className="slide-up-select"
        name={name}
        id={id}
        value={value}
        onChange={onChange}
        IconComponent={WhiteDoubleArrow}
        sx={{
          "&.MuiOutlinedInput-root": {
            color: "white",
            margin: 0,
            "& fieldset": {
              border: "none",
              borderRadius: "4px",
            },
            "&:hover fieldset": {
              border: "none",
              borderRadius: "4px",
            },
            "&.Mui-focused fieldset": {
              border: "none",
              borderRadius: "4px",
            },
          },
          "& .MuiInputBase-input": {
            fontFamily: '"proxima-nova", "sans-serif"',
            backgroundColor: "rgba(255, 255, 255, 0.3)",
            marginRight: "-24px",
          },
          "& .MuiOutlinedInput-root.Mui-focused": {
            "& .MuiInputBase-input": {
              borderBottom: "1px solid white",
            },
          },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

function arePropsEqual(prevProps, nextProps) {
  const {
    options: prevOptions,
    value: prevValue,
    onChange: prevOnChange,
    id: prevId,
    name: prevName,
    width: prevWidth,
    error: prevError,
    helperText: prevHelperText,
  } = prevProps;
  const {
    options: nextOptions,
    value: nextValue,
    onChange: nextOnChange,
    id: nextId,
    name: nextName,
    width: nextWidth,
    error: nextError,
    helperText: nextHelperText,
  } = nextProps;

  const isPrevOnChangeFalsey = !prevOnChange;
  const isNextOnChangeFalsey = !nextOnChange;

  const hasOnChangeChanged = isPrevOnChangeFalsey != isNextOnChangeFalsey;
  const hasOptionsChanged = prevOptions
    ? prevOptions.length != nextOptions.length
    : true;
  const hasValueChanged = prevValue !== nextValue;
  const hasIdChanged = prevId !== nextId;
  const hasNameChanged = prevName !== nextName;
  const hasWidthChanged = prevWidth !== nextWidth;
  const hadErrorChanged = prevError !== nextError;
  const hadHelperTextChanged = prevHelperText !== nextHelperText;

  return (
    !hasValueChanged &&
    !hasOnChangeChanged &&
    !hasOptionsChanged &&
    !hasIdChanged &&
    !hasNameChanged &&
    !hasWidthChanged &&
    !hadErrorChanged &&
    !hadHelperTextChanged
  );
}

export const StandardSelect = memo(StandardSelectComponent, arePropsEqual);

const StandardSelectWithErrorComponent = ({
  options,
  value,
  onChange,
  id,
  name,
  width = 200,
  helperText,
  error = false,
}) => {
  return (
    <FormControl
      sx={{ minWidth: width, marginRight: 0 }}
      size="small"
      error={error}
    >
      <Select
        className="slide-up-select"
        name={name}
        id={id}
        value={value}
        onChange={onChange}
        IconComponent={WhiteDoubleArrow}
        sx={{
          "&.MuiOutlinedInput-root": {
            color: "white",
            margin: 0,
            "& fieldset": {
              border: "none",
              borderRadius: "4px",
            },
            "&:hover fieldset": {
              border: "none",
              borderRadius: "4px",
            },
            "&.Mui-focused fieldset": {
              border: "none",
              borderRadius: "4px",
            },
          },
          "& .MuiInputBase-input": {
            fontFamily: '"proxima-nova", "sans-serif"',
            backgroundColor: "rgba(255, 255, 255, 0.3)",
            marginRight: "-24px",
          },
          "& .MuiOutlinedInput-root.Mui-focused": {
            "& .MuiInputBase-input": {
              borderBottom: "1px solid white",
            },
          },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <FormHelperStyledError>{helperText}</FormHelperStyledError>
      )}
    </FormControl>
  );
};

export const StandardSelectWithError = memo(
  StandardSelectWithErrorComponent,
  arePropsEqual
);
